/**
 * HTTP client for the PersonaMatrix FastAPI Admin API.
 *
 *   CMS (this app) → FastAPI Admin API → PersonaMatrix Core / Supabase
 *
 * Base URL comes from NEXT_PUBLIC_API_URL. Every protected call attaches the
 * stored Supabase access token as `Authorization: Bearer <token>`, which is what
 * the Admin API's `CurrentUser` dependency verifies.
 *
 * All non-2xx responses share one envelope — `{ error: { code, message,
 * status_code, details } }` — so error handling lives here and pages only ever
 * see an `ApiError` with a message worth showing a human.
 */

import { clearSession, getToken } from '@/lib/auth/token';
import { canRefresh, refreshSession } from '@/lib/auth/refresh';
import type { ApiErrorEnvelope } from './types';

/**
 * Where the Admin API lives.
 *
 * WHY THE FALLBACK IS 8002 AND NOT 8000.
 * It used to be 8000, and that turned an unset variable into the worst kind of
 * bug. Under the tunnelled setup local **8000 is the Core API** (`ssh -L
 * 8000:localhost:8000`), and the CMS Admin API runs on 8002. So a missing
 * `NEXT_PUBLIC_API_URL` did not fail — it pointed the whole app at Core, which
 * is listening, accepts the connection, and answers 404 to every
 * `/api/v1/admin/*` route. The UI then reported "cannot reach the Admin API"
 * while something was very much reachable, which sends you looking at CORS and
 * at a service that was never involved.
 *
 * 8002 is the documented port for this service, so an unset variable now at
 * worst points at the right place on the developer's own machine.
 *
 * NOT A HARD FAILURE, DELIBERATELY. Throwing here would be more obvious, but
 * this module is imported by every page and evaluated during prerender, so it
 * would break `next build` for anyone who has not set the variable — including
 * CI runs that only type-check. A loud console error costs nothing and points at
 * the fix; the build staying green is worth more than the extra strictness.
 *
 * Remember this value is **inlined at build time**, not read at runtime: Next
 * substitutes the literal during `next build`, so changing it in `.env` requires
 * restarting the dev server (or rebuilding the image), never just a page reload.
 */
const CONFIGURED_API_URL = process.env.NEXT_PUBLIC_API_URL?.trim();

if (!CONFIGURED_API_URL && typeof window !== 'undefined') {
  console.error(
    '[PersonaMatrix CMS] NEXT_PUBLIC_API_URL is not set, so the CMS is falling back to ' +
      'http://127.0.0.1:8002. Set it in frontend/.env and restart the dev server — the ' +
      'value is compiled in, so a page reload will not pick it up. Note port 8000 is the ' +
      'Core API tunnel, not this service.'
  );
}

export const API_BASE_URL = (CONFIGURED_API_URL || 'http://127.0.0.1:8002').replace(/\/$/, '');

/**
 * SAME-ORIGIN MODE — what the nginx deployment uses.
 *
 * Set `NEXT_PUBLIC_API_URL=/` and this collapses to an empty base, so every call
 * becomes a *relative* `/api/v1/admin/...` that the browser sends to whatever
 * host served the page. nginx then forwards `/api/` to the Admin API container.
 *
 * Two problems disappear at once:
 *
 *   1. CORS. Same origin means no preflight and no `CORS_ORIGINS` list to keep
 *      in step with the server's address.
 *   2. The baked-in host. `NEXT_PUBLIC_*` is inlined at build time, so an
 *      absolute URL welds the image to one IP — move the box and the image is
 *      scrap. `/` is true at every address, so the same image runs anywhere.
 *
 * A value beginning with `/` is also honoured (e.g. `/api-proxy`) for a mount
 * under a sub-path; `/` itself is the ordinary case.
 */
export const API_IS_SAME_ORIGIN = API_BASE_URL === '' || API_BASE_URL.startsWith('/');

/**
 * Where the API is, phrased for a human. `API_BASE_URL` is empty in same-origin
 * mode, and "Cannot reach the Admin API at ." helps nobody.
 */
export const API_LOCATION_LABEL = API_IS_SAME_ORIGIN
  ? 'this origin (proxied to the Admin API by nginx)'
  : API_BASE_URL;

export const API_PREFIX = '/api/v1/admin';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(message: string, status: number, code = 'ERROR', details: unknown = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /** True when the Admin API is up but PersonaMatrix Core is not. */
  get isCoreUnavailable(): boolean {
    return this.status === 503 || this.status === 502 || this.status === 504;
  }

  /** True when the request never reached the server at all. */
  get isNetworkError(): boolean {
    return this.status === 0;
  }
}

export type QueryValue = string | number | boolean | undefined | null;

export interface RequestOptions {
  /**
   * Query-string parameters. Undefined/null/empty values are dropped.
   *
   * An array value becomes a *repeated* parameter (`?sources=a&sources=b`),
   * which is the only encoding FastAPI's `list[str] = Query(...)` accepts — a
   * comma-joined string arrives upstream as one source named "a,b".
   */
  query?: Record<string, QueryValue | QueryValue[]>;
  body?: unknown;
  /** Set false for the public auth endpoints. */
  auth?: boolean;
  signal?: AbortSignal;
  /**
   * Internal. False on the replay of a request that 401'd, so one expired token
   * can be renewed and retried without the retry itself being able to retry.
   */
  retryOnUnauthorized?: boolean;
}

/**
 * `new URL()` requires an absolute base, and in same-origin mode there is none.
 * This placeholder supplies one purely so the query encoder below can be reused;
 * only `pathname + search` is kept, so the host is never emitted and never
 * resolved. `.invalid` is reserved by RFC 2606 and can never be a real host.
 */
const RELATIVE_URL_BASE = 'http://same-origin.invalid';

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const base = API_IS_SAME_ORIGIN ? `${RELATIVE_URL_BASE}${API_BASE_URL}` : API_BASE_URL;
  const url = new URL(`${base}${API_PREFIX}${path}`);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      const values = Array.isArray(value) ? value : [value];
      for (const item of values) {
        if (item === undefined || item === null || item === '') continue;
        url.searchParams.append(key, String(item));
      }
    }
  }
  // Relative on purpose in same-origin mode: the browser resolves it against the
  // page's own origin, which is exactly what nginx is fronting.
  return API_IS_SAME_ORIGIN ? `${url.pathname}${url.search}` : url.toString();
}

/**
 * Pull a human-readable message out of a failed response.
 *
 * Handles the Admin API's own envelope, FastAPI's raw `detail` (which is a list
 * of field errors on a 422 that slipped past the custom handler), and bodies
 * that are not JSON at all.
 */
async function toApiError(response: Response): Promise<ApiError> {
  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch {
    return new ApiError(
      `Request failed with status ${response.status}.`,
      response.status,
      'HTTP_ERROR'
    );
  }

  const envelope = payload as Partial<ApiErrorEnvelope> & { detail?: unknown };

  if (envelope?.error?.message) {
    return new ApiError(
      envelope.error.message,
      response.status,
      envelope.error.code || 'ERROR',
      envelope.error.details ?? null
    );
  }

  if (typeof envelope?.detail === 'string') {
    return new ApiError(envelope.detail, response.status, 'HTTP_ERROR');
  }

  if (Array.isArray(envelope?.detail)) {
    const message = envelope.detail
      .map((item) => {
        const entry = item as { loc?: unknown[]; msg?: string };
        const field = Array.isArray(entry.loc) ? entry.loc.slice(1).join('.') : '';
        return field ? `${field}: ${entry.msg}` : entry.msg;
      })
      .filter(Boolean)
      .join('; ');
    return new ApiError(message || 'Validation failed.', response.status, 'VALIDATION_ERROR');
  }

  return new ApiError(`Request failed with status ${response.status}.`, response.status);
}

/**
 * A 401 is not always the end of a session.
 *
 * An access token expires an hour after sign-in, and until this existed that
 * moment ended the session: the client cleared the token and every open panel
 * filled with "your session has expired". But the login response also carried a
 * Supabase refresh token, so the usual cause of a 401 — an expired token — is
 * recoverable without the operator doing anything.
 *
 * So a 401 on an authenticated request tries exactly one renewal and replays
 * the request. Only if the renewal fails (no configuration, or Supabase
 * refusing the refresh token) is the session cleared, which is the behaviour
 * this replaced. One retry, never a loop: the replay is issued with
 * `retryOnUnauthorized: false`.
 */
async function request<T>(method: string, path: string, options: RequestOptions = {}): Promise<T> {
  const { query, body, auth = true, signal, retryOnUnauthorized = true } = options;

  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  if (auth) {
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  } catch (error) {
    // fetch only rejects on transport failure, so this is "the API is not
    // running / CORS refused the request", never an HTTP error status.
    throw new ApiError(
      `Cannot reach the Admin API at ${API_LOCATION_LABEL}. Is it running, and is this origin allowed by CORS_ORIGINS?`,
      0,
      'NETWORK_ERROR',
      error
    );
  }

  if (response.status === 401 && auth) {
    // Renew once, then replay. See the note above `request`.
    if (retryOnUnauthorized && canRefresh() && (await refreshSession())) {
      return request<T>(method, path, { ...options, retryOnUnauthorized: false });
    }
    // The token is gone, expired and unrenewable, or genuinely rejected. Drop it
    // so the middleware sends the next navigation to /login rather than looping
    // through failing requests.
    clearSession();
    throw await toApiError(response);
  }

  if (!response.ok) throw await toApiError(response);

  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T;
  }

  return (await response.json()) as T;
}

/**
 * POST whose *response* is binary rather than JSON.
 *
 * The voice preview route returns the synthesised audio itself — `audio/mpeg`
 * for ElevenLabs, `audio/wav` for Gnani and Sarvam — because Core generates the
 * bytes and has nowhere to publish them to. There is no URL to hand back, and
 * inventing one would mean this stack storing and serving audio that nothing
 * needs to keep.
 *
 * Separate from `request` for one reason: `request` ends in `response.json()`,
 * which throws on an MP3. Error handling is shared — a failure still arrives as
 * an `ApiError` carrying the backend's own message — because a 409 "clone this
 * voice first" is a JSON envelope even on a route whose success is audio.
 */
async function requestBlob(
  method: string,
  path: string,
  options: RequestOptions = {}
): Promise<Blob> {
  const { query, body, auth = true, signal } = options;

  // `Accept: audio/*` would be a lie about the failure case: an error on this
  // route is still the JSON envelope, and a strict server could refuse it.
  const headers: Record<string, string> = { Accept: 'audio/*, application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  if (auth) {
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  } catch (error) {
    throw new ApiError(
      `Cannot reach the Admin API at ${API_LOCATION_LABEL}. Is it running, and is this origin allowed by CORS_ORIGINS?`,
      0,
      'NETWORK_ERROR',
      error
    );
  }

  if (response.status === 401 && auth) {
    clearSession();
    throw await toApiError(response);
  }
  if (!response.ok) throw await toApiError(response);

  const blob = await response.blob();
  // A zero-byte body is not "audio with no sound" — handed to an <audio>
  // element it fails with no visible cause, which reads as a broken button.
  if (blob.size === 0) {
    throw new ApiError(
      'The server returned an empty audio response.',
      response.status,
      'EMPTY_BODY'
    );
  }
  return blob;
}

/**
 * Multipart POST, for the one route that takes file uploads (cast anchors).
 *
 * Separate from `request` because the two disagree on one detail that matters:
 * `Content-Type` must NOT be set here. The browser has to generate it itself so it
 * can append the multipart boundary — setting `multipart/form-data` by hand
 * produces a body the server cannot parse, and the failure looks like a validation
 * error rather than an encoding one.
 */
async function requestForm<T>(
  method: string,
  path: string,
  body: FormData,
  options: Omit<RequestOptions, 'body'> = {}
): Promise<T> {
  const { query, auth = true, signal } = options;

  const headers: Record<string, string> = { Accept: 'application/json' };
  if (auth) {
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), { method, headers, body, signal });
  } catch (error) {
    throw new ApiError(
      `Cannot reach the Admin API at ${API_LOCATION_LABEL}. Is it running, and is this origin allowed by CORS_ORIGINS?`,
      0,
      'NETWORK_ERROR',
      error
    );
  }

  if (response.status === 401 && auth) {
    clearSession();
    throw await toApiError(response);
  }
  if (!response.ok) throw await toApiError(response);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) => request<T>('GET', path, options),
  postForm: <T>(path: string, body: FormData, options?: Omit<RequestOptions, 'body'>) =>
    requestForm<T>('POST', path, body, options),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('POST', path, { ...options, body }),
  /** POST returning binary — the voice preview, whose response *is* the audio. */
  postBlob: (path: string, body?: unknown, options?: RequestOptions) =>
    requestBlob('POST', path, { ...options, body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('PATCH', path, { ...options, body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('PUT', path, { ...options, body }),
  delete: <T>(path: string, options?: RequestOptions) => request<T>('DELETE', path, options),
};

/** Health probe. Lives outside `/api/v1/admin`, so it bypasses `api`. */
export async function fetchHealth(): Promise<{ status: string; [key: string]: unknown }> {
  const response = await fetch(`${API_BASE_URL}/health`);
  if (!response.ok) throw await toApiError(response);
  return response.json();
}

/** Turn anything thrown by a query/mutation into a message a page can render. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return 'Something went wrong.';
}

/**
 * Is this a 404 — that is, an *answer* rather than a failure?
 *
 * Several of Core's character-scoped routes use 404 to mean a real, common
 * state: `/characters/{id}/persona` for a character no persona is bound to,
 * `/{id}/voice` for one with no extracted sample, `/{id}/objects` and
 * `/{id}/wardrobe` for the same missing binding. Those pages must render the
 * state, with its cause, rather than an error — and the check was being written
 * inline as `error instanceof ApiError && error.status === 404` in four places,
 * one of which had already drifted into reading `.status` off an unnarrowed
 * `unknown`.
 */
export function isNotFound(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}
